/**
 * AddToTransportModal
 *
 * Modal that lets the user pick a transport (or create a new one) and attach
 * an object to it.  Used by the Node Definitions, Locks, Events and
 * Scheduling Windows list screens.
 */
import React, { useEffect, useState } from 'react';
import { Button, Form, Modal, Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import { AppDispatch, RootState, useAppDispatch } from 'redux/store';
import { fetchTransports, createTransport, addTransportItem, TransportItem } from 'redux/slices/transportsSlice';

interface Props {
  show: boolean;
  onClose: () => void;
  objectType: TransportItem['object_type'];
  objectId?: number;
  objectIds?: number[];
  objectLabel?: string;
}

const AddToTransportModal: React.FC<Props> = ({ show, onClose, objectType, objectId, objectIds, objectLabel }) => {
  const dispatch = useAppDispatch();
  const { transports, loading } = useSelector((s: RootState) => s.transports);

  const [mode, setMode] = useState<'existing' | 'new'>('existing');
  const [selectedTransportId, setSelectedTransportId] = useState<number | ''>('');
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [includeRelatedObjects, setIncludeRelatedObjects] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (show) {
      dispatch(fetchTransports());
      setMode('existing');
      setSelectedTransportId('');
      setNewName('');
      setNewDescription('');
      setIncludeRelatedObjects(false);
    }
  }, [show, dispatch]);

  const handleSave = async () => {
    setSubmitting(true);
    try {
      const ids = Array.isArray(objectIds) && objectIds.length > 0 ? objectIds : objectId !== undefined ? [objectId] : [];
      if (ids.length === 0) return;

      let transportId: number;
      if (mode === 'new') {
        if (!newName.trim()) return;
        const result = await dispatch(
          createTransport({
            name: newName.trim(),
            description: newDescription || undefined,
          })
        );
        if (createTransport.rejected.match(result)) return;
        transportId = (result.payload as any).id;
      } else {
        if (!selectedTransportId) return;
        transportId = Number(selectedTransportId);
      }

      for (const id of ids) {
        await dispatch(
          addTransportItem({
            transportId,
            object_type: objectType,
            object_id: id,
            include_related_objects: includeRelatedObjects,
          })
        );
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const isBusy = submitting || loading.fetchTransports || loading.createTransport || loading.addItem;

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>Add to Transport</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {objectLabel && (
          <p className="text-muted mb-3">
            Adding <strong>{objectLabel}</strong> ({objectType.replace('_', ' ')}) to a transport.
          </p>
        )}
        <Form.Group className="mb-3">
          <Form.Label>Transport</Form.Label>
          <div className="d-flex gap-3 mb-2">
            <Form.Check type="radio" id="mode-existing" label="Select existing" checked={mode === 'existing'} onChange={() => setMode('existing')} />
            <Form.Check type="radio" id="mode-new" label="Create new" checked={mode === 'new'} onChange={() => setMode('new')} />
          </div>

          {mode === 'existing' && (
            <Form.Select
              value={selectedTransportId}
              onChange={(e) => setSelectedTransportId(e.target.value ? Number(e.target.value) : '')}
              disabled={loading.fetchTransports}
            >
              <option value="">-- Select a transport --</option>
              {transports.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} {t.item_count !== undefined ? `(${t.item_count} items)` : ''}
                </option>
              ))}
            </Form.Select>
          )}

          {mode === 'new' && (
            <>
              <Form.Control type="text" placeholder="Transport name" value={newName} onChange={(e) => setNewName(e.target.value)} className="mb-2" />
              <Form.Control
                as="textarea"
                rows={2}
                placeholder="Description (optional)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </>
          )}
        </Form.Group>

        <Form.Group className="mb-2">
          <Form.Check
            type="checkbox"
            id="include-related-objects"
            label="Include related objects"
            checked={includeRelatedObjects}
            onChange={(e) => setIncludeRelatedObjects(e.target.checked)}
          />
          <Form.Text className="text-muted">
            When enabled, dependencies (zone, queue, scheduling windows, connections, workers, etc.) are added automatically.
          </Form.Text>
        </Form.Group>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onClose} disabled={isBusy}>
          Close
        </Button>
        <Button
          variant="primary"
          onClick={handleSave}
          disabled={isBusy || (mode === 'existing' && !selectedTransportId) || (mode === 'new' && !newName.trim())}
        >
          {isBusy ? <Spinner size="sm" /> : 'Add to Transport'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default AddToTransportModal;
